import React, { useState, useRef } from 'react';

const SipCalculatorSection = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(15000);
  const [investmentPeriod, setInvestmentPeriod] = useState(15);
  const [expectedReturn, setExpectedReturn] = useState(15);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const resultRef = useRef(null);

  const calculateSIP = () => {
    const monthlyRate = expectedReturn / 100 / 12;
    const months = investmentPeriod * 12;
    const futureValue = monthlyInvestment * (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
    const totalInvestment = monthlyInvestment * months;
    const wealthGained = futureValue - totalInvestment;
    
    // Generate chart data
    const chartPoints = [];
    for (let year = 0; year <= investmentPeriod; year++) {
      const monthsElapsed = year * 12;
      const investedAmount = monthlyInvestment * monthsElapsed;
      let accumulatedAmount = 0;
      
      if (monthsElapsed > 0) {
        accumulatedAmount = monthlyInvestment * (((Math.pow(1 + monthlyRate, monthsElapsed) - 1) / monthlyRate) * (1 + monthlyRate));
      }
      
      chartPoints.push({
        year,
        invested: investedAmount,
        accumulated: accumulatedAmount
      });
    }
    
    setChartData(chartPoints);
    setResult({
      futureValue: Math.round(futureValue),
      totalInvestment,
      wealthGained: Math.round(wealthGained)
    });

    // Scroll to results section
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const styles = {
    sipCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease',
      '&:focus': {
        borderColor: '#3b82f6'
      }
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    resultSection: {
      marginTop: '3rem',
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '15px',
      border: '1px solid #e5e7eb'
    },
    chartSection: {
      marginBottom: '2rem'
    },
    chartTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#1a1a1a',
      marginBottom: '1rem',
      textAlign: 'center'
    },
    chartContainer: {
      position: 'relative',
      height: '400px',
      backgroundColor: 'white',
      borderRadius: '10px',
      border: '1px solid #e5e7eb',
      padding: '20px',
      marginBottom: '2rem'
    },
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '1rem',
      textAlign: 'center'
    },
    resultItem: {
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    resultLabel: {
      fontSize: '0.9rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      fontWeight: '500'
    },
    resultValue: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: '#1a1a1a'
    },
    resultText: {
      backgroundColor: '#e3f2fd',
      padding: '1.5rem',
      borderRadius: '12px',
      fontSize: '1rem',
      lineHeight: '1.6',
      color: '#1a1a1a',
      marginTop: '1rem'
    },
    resultNote: {
      fontSize: '0.85rem',
      color: '#6b7280',
      fontStyle: 'italic',
      marginTop: '1rem'
    }
  };

  // SVG Chart Component
  const GrowthChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => d.accumulated));
    const chartWidth = 760;
    const chartHeight = 300;
    const padding = 60;
    const plotWidth = chartWidth - padding * 2;
    const plotHeight = chartHeight - padding * 2;

    const xScale = (year) => padding + (year / investmentPeriod) * plotWidth;
    const yScale = (value) => chartHeight - padding - (value / maxValue) * plotHeight;

    // Create path for invested amount (lower area)
    const investedPath = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.invested)}`
    ).join(' ') + ` L ${xScale(investmentPeriod)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

    // Create path for accumulated amount (upper area)
    const accumulatedPath = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.accumulated)}`
    ).join(' ') + ` L ${xScale(investmentPeriod)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

    // Create line for accumulated amount
    const accumulatedLine = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.accumulated)}`
    ).join(' ');

    // Generate Y-axis labels
    const yLabels = [];
    for (let i = 0; i <= 6; i++) {
      const value = (maxValue / 6) * i;
      yLabels.push({
        value: Math.round(value),
        y: yScale(value)
      });
    }

    return (
      <svg width="100%" height="400" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width={chartWidth} height={chartHeight} fill="url(#grid)" />
        
        {/* Y-axis labels */}
        {yLabels.map((label, i) => (
          <g key={i}>
            <line x1={padding} y1={label.y} x2={chartWidth - padding} y2={label.y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padding - 10} y={label.y + 5} textAnchor="end" fontSize="12" fill="#6b7280">
              {(label.value / 1000000).toFixed(1)}M
            </text>
          </g>
        ))}
        
        {/* X-axis labels */}
        {data.map((d, i) => (
          <g key={i}>
            {i % 2 === 0 && (
              <>
                <line x1={xScale(d.year)} y1={chartHeight - padding} x2={xScale(d.year)} y2={chartHeight - padding + 5} stroke="#6b7280" strokeWidth="1" />
                <text x={xScale(d.year)} y={chartHeight - padding + 20} textAnchor="middle" fontSize="12" fill="#6b7280">
                  {d.year}
                </text>
              </>
            )}
          </g>
        ))}
        
        {/* Invested amount area */}
        <path d={investedPath} fill="#3b82f6" opacity="0.6" />
        
        {/* Accumulated amount area */}
        <path d={accumulatedPath} fill="#06b6d4" opacity="0.3" />
        
        {/* Accumulated amount line */}
        <path d={accumulatedLine} fill="none" stroke="#06b6d4" strokeWidth="2" />
        
        {/* Axis labels */}
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151">
          Period in Years
        </text>
        <text x={25} y={chartHeight / 2} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151" transform={`rotate(-90, 25, ${chartHeight / 2})`}>
          Amount (Rs.)
        </text>
        
        {/* Legend */}
        <g transform={`translate(${chartWidth - 200}, 30)`}>
          <rect x="0" y="0" width="15" height="15" fill="#06b6d4" />
          <text x="20" y="12" fontSize="12" fill="#374151">Amount Accumulated</text>
          <rect x="0" y="25" width="15" height="15" fill="#3b82f6" />
          <text x="20" y="37" fontSize="12" fill="#374151">Amount Invested</text>
        </g>
      </svg>
    );
  };

  return (
    <>
      <style>{`
        .sip-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .sip-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .sip-calculate-btn:hover {
          background-color:rgb(38, 220, 68) !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
      `}</style>
      
      <div style={styles.sipCalculatorContent}>
        <p style={styles.description}>
          It tells you how much wealth you can create by making monthly investment.
        </p>
        
        {/* Monthly Investment */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Monthly Investment (Rs.)
            <input
              type="number"
              min="500"
              max="1000000"
              step="500"
              value={monthlyInvestment}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setMonthlyInvestment('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setMonthlyInvestment(Math.max(500, Math.min(100000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setMonthlyInvestment(15000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="500"
              max="1000000"
              step="500"
              value={monthlyInvestment || 15000}
              onChange={(e) => setMonthlyInvestment(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              <span>500</span>
              <span>250,375</span>
              <span>500,250</span>
              <span>750,125</span>
              <span>1,000,000</span>
            </div>
          </div>
        </div>

        {/* Investment Period */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Investment period (in years)
            <input
              type="number"
              min="1"
              max="50"
              step="1"
              value={investmentPeriod}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInvestmentPeriod('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setInvestmentPeriod(Math.max(1, Math.min(50, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setInvestmentPeriod(15);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter years"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="50"
              step="1"
              value={investmentPeriod || 15}
              onChange={(e) => setInvestmentPeriod(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>5</span>
              <span>15</span>
              <span>25</span>
              <span>35</span>
              <span>45</span>
              <span>50</span>
            </div>
          </div>
        </div>

        {/* Expected Rate of Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected rate of return (% p.a)
            <input
              type="number"
              min="1"
              max="30"
              step="0.1"
              value={expectedReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExpectedReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setExpectedReturn(Math.max(1, Math.min(30, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setExpectedReturn(15);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="30"
              step="0.1"
              value={expectedReturn || 15}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>3.9</span>
              <span>6.8</span>
              <span>9.7</span>
              <span>12.6</span>
              <span>15.5</span>
              <span>18.4</span>
              <span>21.3</span>
              <span>24.2</span>
              <span>27.1</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="sip-calculate-btn"
          onClick={calculateSIP}
        >
          Calculate
        </button>

        {/* Results */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            {/* Growth Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.chartTitle}>Growth Chart</h3>
              <GrowthChart data={chartData} />
            </div>

            {/* Result Summary */}
            <div style={styles.resultGrid}>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Total Investment</div>
                <div style={styles.resultValue}>₹{result.totalInvestment.toLocaleString()}</div>
              </div>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Wealth Gained</div>
                <div style={styles.resultValue}>₹{result.wealthGained.toLocaleString()}</div>
              </div>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Future Value</div>
                <div style={styles.resultValue}>₹{result.futureValue.toLocaleString()}</div>
              </div>
            </div>

            {/* Result Description */}
            <div style={styles.resultText}>
              If you invest Rs. <strong>{monthlyInvestment.toLocaleString()}/-</strong> per month for a period of <strong>{investmentPeriod}</strong> years, 
              you can create your wealth equivalent to Rs. <strong>{result.futureValue.toLocaleString()}/-</strong>. 
              Total investment in this period is Rs. <strong>{result.totalInvestment.toLocaleString()}/-</strong>.
            </div>

            <div style={styles.resultNote}>
              Note: Based on monthly compounding
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SipCalculatorSection;